/**
 * Analytics Tracking Utility
 * PostHog integration for user behavior tracking.
 * Privacy-first: no PII collected, EU-hosted.
 */
import posthog from 'posthog-js';

// ─── Core Tracking ──────────────────────────────────────
export const trackEvent = (eventName: string, payload?: Record<string, any>) => {
  posthog.capture(eventName, payload);
  if (import.meta.env.DEV) {
    console.log(`[ANALYTICS: ${eventName}]`, payload || '');
  }
};

// ─── User Identification ────────────────────────────────
export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  posthog.identify(userId, traits);
};

export const resetUser = () => {
  posthog.reset();
};

// ─── Game Events ────────────────────────────────────────
export const trackGameStarted = (realm: string, isSolo: boolean, difficulty?: string, questionCount?: number) =>
  trackEvent('game_started', { realm, isSolo, difficulty, questionCount });

export const trackQuestionAnswered = (questionId: string, isCorrect: boolean, timeMs: number, realm?: string, difficulty?: string) =>
  trackEvent('question_answered', { questionId, isCorrect, timeMs, realm, difficulty });

export const trackGameCompleted = (realm: string, isSolo: boolean, totalScore: number, accuracy?: number, avgSpeed?: number, archetype?: string) =>
  trackEvent('game_completed', { realm, isSolo, totalScore, accuracy, avgSpeed, archetype });

export const trackEnduranceDeath = (round: number, score: number) =>
  trackEvent('endurance_death', { round, score });

export const trackRematchRequested = (realm: string) =>
  trackEvent('rematch_requested', { realm });

// ─── Engagement Events ──────────────────────────────────
export const trackRealmSelected = (realm: string) =>
  trackEvent('realm_selected', { realm });

export const trackShareTapped = (screen: string, archetype?: string) =>
  trackEvent('share_tapped', { screen, archetype });

// ─── PM Funnel Events ───────────────────────────────────
export const trackSnapshotViewed = (archetype: string, accuracy: number) =>
  trackEvent('snapshot_viewed', { archetype, accuracy });

export const trackPMCtaClicked = (placement: string, utmContent?: string) =>
  trackEvent('pm_cta_clicked', { placement, utm_content: utmContent });

export const trackVideoAdStarted = (video: string, placement: string) =>
  trackEvent('video_ad_started', { video, placement });

export const trackVideoAdCompleted = (video: string, placement: string) =>
  trackEvent('video_ad_completed', { video, placement });

export const trackVideoAdSkipped = (video: string, placement: string, skipAtSecond: number) =>
  trackEvent('video_ad_skipped', { video, placement, skipAtSecond });

// ─── Auth Events ────────────────────────────────────────
export const trackSignIn = (method: string) =>
  trackEvent('sign_in_completed', { method });

export const trackGuestPlay = () =>
  trackEvent('guest_play', {});
